import { signalStore, withMethods, withState, patchState, withComputed } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { EMPTY, forkJoin, map, pipe, switchMap, tap } from 'rxjs';
import { computed, inject } from '@angular/core';

import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

import { MediaPreview } from './placement-media/components/media-preview/models';
import {
	ScheduleBatchUpdateRequest,
	ScheduleBatchUpdateResponse,
} from 'src/app/features/shared/models/legacy/Schedule';

import { Approval } from 'src/app/features/shared/components/approvers/models';
import { DialogService } from '@ngneat/dialog';
import { Approver, Zone, Color } from 'src/app/features/shared/models';
import { NeptuneError } from 'src/app/features/shared/models/Neptune';
import { BookingMediaResponse } from 'src/app/features/shared/models/legacy/BookingMediaResponse';
import { PresignedUploadData } from 'src/app/features/shared/models/legacy/UploadMediaRequest';

export interface BookingMediaState {
	tenant: number;
	booking: string;
	campaign: string;
	touchpoints: any[];
	colors: Color[];
	available: MediaPreview[];
	assigned: MediaPreview[];
	approvers: Approver[];
	filters: {
		search: string;
	};
	status: 'LOADING' | 'SUCCESS' | 'ERROR';
	error: NeptuneError | null;
}

const initialState: BookingMediaState = {
	tenant: -1,
	booking: '',
	campaign: '',
	touchpoints: [],
	colors: [],
	available: [],
	assigned: [],
	approvers: [],
	filters: {
		search: '',
	},
	status: 'LOADING',
	error: null,
};

export const BookingMediaStore = signalStore(
	withState(initialState),
	withMethods((store, http = inject(HttpClient), dialog = inject(DialogService)) => ({
		retrieveMedia: rxMethod<{ bookingId: string; tenantId: number; campaignId: string }>(
			pipe(
				tap(() =>
					patchState(store, { booking: '', available: [], assigned: [], filters: { search: '' }, status: 'LOADING' }),
				),
				switchMap(({ bookingId, tenantId, campaignId }: { bookingId: string; tenantId: number; campaignId: string }) =>
					forkJoin({
						media: http.get<BookingMediaResponse>(
							environment.api + `/client_portal/${tenantId}/campaigns/${campaignId}/placements/${bookingId}/media/`,
						),
						touchpoints: http.get<any>(
							environment.api + `/client_portal/campaigns/initialize/?holdingCompanyId=${tenantId}`,
						),
						approvers: http.get<any[]>(environment.api + `/client_portal/${tenantId}/approvers/`),
					}).pipe(
						tap({
							next: ({ media, approvers, touchpoints }) => {
								patchState(store, {
									tenant: tenantId,
									booking: bookingId,
									campaign: campaignId,
									touchpoints: touchpoints.touchpoints,
									colors: touchpoints.colors?.map((color: any) => ({ ...color, colour: color.color })),
									available: media.library,
									assigned: media.assigned,
									approvers: approvers.map((approver) => ({
										id: approver.id,
										name: approver.firstName + ' ' + approver.lastName,
									})),
									status: 'SUCCESS',
									error: null,
								});
							},
							error: (error: NeptuneError) =>
								patchState(store, {
									status: 'ERROR',
									error: error,
								}),
						}),
					),
				),
			),
		),
		uploadMedia: rxMethod<{ files: any[]; approverId: number[]; lighting: { zones: Zone[] } | null }>(
			pipe(
				switchMap(({ files, approverId, lighting }) => {
					const presignedRequests = files.map((file) =>
						http
							.get<
								PresignedUploadData[]
							>(environment.api + `/client_portal/${store.tenant()}/campaigns/${store.campaign()}/placements/${store.booking()}/media/upload/?files=${file.file.name}`)
							.pipe(
								switchMap((response: PresignedUploadData[]) => {
									const data = new FormData();
									Object.keys(response[0].uploadData.fields).forEach((key) => {
										data.append(key, response[0].uploadData.fields[key]);
									});

									data.append('file', file.file);
									return http
										.post<FormData>(response[0].uploadData.url, data)
										.pipe(map(() => ({ file, uploadData: response })));
								}),
							),
					);

					return forkJoin(presignedRequests).pipe(
						switchMap((uploadResults: { file: any; uploadData: PresignedUploadData[] }[]) => {
							const data = new FormData();
							data.append('approvers', JSON.stringify(approverId));

							if (lighting) {
								data.append('lighting', JSON.stringify(lighting));
							}

							uploadResults.forEach(({ file, uploadData }) => {
								const fileKey = uploadData[0].uploadData.fields['key'];
								if (fileKey) {
									data.append(file.variant, fileKey);
								}
							});

							return http.post<MediaPreview[]>(
								environment.api +
									`/client_portal/${store.tenant()}/campaigns/${store.campaign()}/placements/${store.booking()}/media/upload/`,
								data,
							);
						}),
						tap({
							next: (media: MediaPreview[]) => {
								const mediaWithLighting = (Array.isArray(media) ? media : [media]).map((mediaItem) => {
									if (!mediaItem.lighting && lighting) {
										return {
											...mediaItem,
											lighting: {
												zones: lighting.zones.map((zone: any) => ({
													id: zone.id,
													name: `Zone ${zone.id}`,
													colour: zone.color,
												})),
											},
										};
									}
									return mediaItem;
								});

								patchState(store, {
									assigned: [...(store.assigned() || []), ...mediaWithLighting],
									status: 'SUCCESS',
								});

								dialog.closeAll();
							},
							error: (error: NeptuneError) => patchState(store, { status: 'ERROR', error }),
						}),
					);
				}),
			),
		),
		assignMedia: rxMethod<{ media: MediaPreview }>(
			pipe(
				tap(() => patchState(store, { status: 'LOADING' })),
				switchMap(({ media }) =>
					http
						.post<MediaPreview>(
							environment.api +
								`/client_portal/${store.tenant()}/campaigns/${store.campaign()}/placements/${store.booking()}/media/${media.uuid}/assign/`,
							{},
						)
						.pipe(
							tap({
								next: () =>
									patchState(store, {
										assigned: [...(store.assigned() || []), media],
										available:
											store.available().map((item) => (item.id === media.id ? { ...item, selected: true } : item)) ||
											[],
										status: 'SUCCESS',
									}),
								error: (error: NeptuneError) => patchState(store, { status: 'ERROR', error }),
							}),
						),
				),
			),
		),
		unassignMedia: rxMethod<{ media: MediaPreview }>(
			pipe(
				tap(() => patchState(store, { status: 'LOADING' })),
				switchMap(({ media }) =>
					http
						.delete<MediaPreview>(
							environment.api +
								`/client_portal/${store.tenant()}/campaigns/${store.campaign()}/placements/${store.booking()}/media/${media.uuid}/assign/`,
						)
						.pipe(
							tap({
								next: () =>
									patchState(store, {
										assigned: store.assigned().filter((m) => m.id !== media.id),
										available:
											store.available().map((item) => (item.id === media.id ? { ...item, selected: false } : item)) ||
											[],
										status: 'SUCCESS',
									}),
								error: (error: NeptuneError) => patchState(store, { status: 'ERROR', error }),
							}),
						),
				),
			),
		),
		updateMedia: rxMethod<{ mediaId: string; approvers: number[]; lighting: { zones: Zone[] } | null }>(
			pipe(
				switchMap(({ mediaId, approvers, lighting }) => {
					if (!lighting && !approvers) {
						dialog.closeAll();
						return EMPTY;
					}

					const data = new FormData();
					if (lighting) {
						data.append('lighting', JSON.stringify(lighting));
					}

					if (approvers) {
						data.append('approvers', JSON.stringify(approvers));
					}

					const media = store.assigned().find((m) => m.uuid === mediaId);
					if (media) {
						data.append('mediaEventId', media.mediaEventId.toString());
					}

					return http
						.patch<MediaPreview>(
							environment.api +
								`/client_portal/${store.tenant()}/campaigns/${store.campaign()}/placements/${store.booking()}/media/upload/`,
							data,
						)
						.pipe(
							tap({
								next: () => {
									const assigned = store.assigned();
									const media = assigned.find((m) => m.uuid === mediaId);

									if (media && (lighting || approvers)) {
										if (lighting) {
											media.lighting = {
												zones: lighting.zones.map((zone: any) => ({
													id: zone.id,
													name: `Zone ${zone.id}`,
													colour: zone.color,
												})),
											};
										}

										if (approvers) {
											media.approvals = store
												.approvers()
												.filter((approver) => approvers.includes(approver.id))
												.map((approver, index) => ({
													id: index,
													approver: {
														id: approver.id,
														name: approver.name,
														avatar: '',
													},
													approved: null,
												}));
										}

										const updated = [...assigned.filter((m) => m.uuid !== mediaId), media];

										patchState(store, {
											assigned: updated,
											status: 'SUCCESS',
										});
									}

									dialog.closeAll();
								},
								error: (error: NeptuneError) => patchState(store, { status: 'ERROR', error }),
							}),
						);
				}),
			),
		),
		updateSchedules: rxMethod<{ schedules: ScheduleBatchUpdateRequest }>(
			pipe(
				tap(() => patchState(store, { status: 'LOADING' })),
				switchMap(({ schedules }) =>
					http
						.post<ScheduleBatchUpdateResponse>(`${environment.api}/client_portal/bulk_schedule/`, {
							update: schedules.update,
							delete: schedules.delete,
							create: schedules.create,
						})
						.pipe(
							tap({
								next: (response) => {
									const creates = schedules.create.map((createRequest) => {
										const responseItem = response.find((r) => r.localId === createRequest.localId);
										return {
											id: responseItem?.id || -1,
											scheduleId: responseItem?.id || -1,
											mediaEventId: createRequest.mediaEventId,
											start: new Date(createRequest.start),
											end: new Date(createRequest.end),
										};
									});

									const updates = schedules.update.map((updateRequest) => {
										const responseItem = response.find((r) => r.id === updateRequest.id);
										return {
											id: responseItem?.id || updateRequest.id,
											scheduleId: responseItem?.id || updateRequest.id,
											start: new Date(updateRequest.start),
											end: new Date(updateRequest.end),
										};
									});

									patchState(store, {
										assigned: store.assigned().map((media) => {
											const mediaSchedules = creates.filter((schedule) => schedule.mediaEventId === media.mediaEventId);

											const updatedSchedules = media.schedules.map((schedule) => {
												const update = updates.find((u) => u.id === schedule.id);
												return update || schedule;
											});

											const remainingSchedules = updatedSchedules.filter(
												(schedule) => !schedules.delete.includes(schedule.id),
											);

											return {
												...media,
												schedules: [...remainingSchedules, ...mediaSchedules],
											};
										}),
										status: 'SUCCESS',
									});
								},
								error: (error: NeptuneError) => {
									patchState(store, { status: 'ERROR', error });
								},
							}),
						),
				),
			),
		),
		setSearch(searchTerm: string) {
			patchState(store, { ['filters']: { ...store['filters'](), search: searchTerm } });
		},
	})),
	withComputed((store) => ({
		availableMedia: computed(() => {
			const searchTerm = store.filters().search?.toLowerCase();
			const available = store.available();

			if (!searchTerm) {
				return available.filter((item) => !item.selected);
			}

			return available.filter((item) => {
				const searchableFields = [item.name];
				return searchableFields.some((field) => field?.toLowerCase().includes(searchTerm)) && !item.selected;
			});
		}),
		scheduleResources: computed(() =>
			store.assigned().map((media) => ({
				id: media.mediaEventId,
				mediaEventId: media.mediaEventId,
				mediaId: media.id,
				name: media.name,
				image: media.preview.image,
				video: media.preview.video,
				resolution: `${media.resolution.width} x ${media.resolution.height}`,
				duration: media.duration,
				orientation: media.orientation,
				variants: media.linkedMedia.length,
				approved: media.approvals.every((approval: Approval) => approval.approved),
			})),
		),
		scheduleEvents: computed(() =>
			store.assigned().flatMap(
				(media) =>
					media.schedules?.map((schedule) => ({
						name: media.name,
						resourceId: media.mediaEventId,
						scheduleId: schedule.id,
						startDate: new Date(schedule.start),
						endDate: new Date(schedule.end),
						dirty: false,
					})) ?? [],
			),
		),
	})),
);
